import { FormLayout } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import { getKey } from "../../helpers/sessionKey";
import { hasAccess } from "../../utils/permission";
import EditDeliveryCharge from "../EditDeliveryCharge";
import Button from "../Reusable/Button";
import ComboboxInput from "../Reusable/ComboboxInput";
import InputCell from "../Reusable/DataTable/InputCell";
import NACell from "../Reusable/DataTable/NACell";
import TrackingCell from "../Reusable/DataTable/TrackingCell";
import DateRangePicker from "../Reusable/DateRangerPicker";
import Modal from "../Reusable/Modal";
import MultiSelectFilterOrderStatus from "../Reusable/MultiSelectFilterOrderStatus";
import CargoBranchInvoicePopup from "./CargoBranchInvoicePopup";
import CreateBranchForm from "./CreateBranchForm";
import InvoiceDataTable from "../Reusable/InvoiceDataTable";

const CreateBranchInvoice = (props: any) => {
  const {
    handleCreate,
    isLoading,
    currentUser,
    userPermissions,
    isDraftSaveLoading,
    handleSaveDraft,
  } = props;
  const [id, setId] = useState();
  const [chargevalue, setChargeValue] = useState();
  const [openDeliveryModal, setOpenDeliveryModal] = useState(false);
  const [searchParams, setSearchParms] = useSearchParams();
  const [openCreateInvoicePopup, setOpenCreateInvoicePopup] = useState(false);
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const [data, setData] = useState([]);

  const [invoiceReport, setInvoiceReport] = useState({
    totalCollectedCod: null,
    totalBranchCharge: null,
    totalTransitCharge: null,
    totalOrder: null,
    totalAmount: null,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    getValues,
    clearErrors,
    control,
  } = useForm<any>({
    defaultValues: async () => ({
      from_branch: currentUser?.default_branch,
    }),
  });

  const FromBranch = watch("from_branch");
  const ToBranch = watch("to_branch");

  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems || selectedItems === undefined) {
      searchParams.set("selectedItems", value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        searchParams.set("selectedItems", newSelectedItems);
      } else {
        searchParams.delete("selectedItems");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      setSearchParms(searchParams);
    }
  };

  let defaultColumns: any = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <div className={tw`flex justify-center w-full`}>
            <input
              type="checkbox"
              checked={checkedAllItems}
              onChange={() => handleSelectAll(table)}
              className={tw`cursor-pointer`}
            />
          </div>
        );
      },
      cell: ({ row }: any) => (
        <div className={tw`flex justify-center w-full`}>
          <input
            type="checkbox"
            checked={isSelected(row?.original?.id)}
            onChange={() => handleIndividualSelect(row?.original?.id)}
            className={tw`cursor-pointer`}
          />
        </div>
      ),
    },
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: TrackingCell,
    },
    {
      header: "Origin",
      accessorKey: "pickup_branch.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender.full_name",
      cell: NACell,
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw``}>
              {row?.original?.receiver.full_name ?? "N/A"}{" "}
            </p>
            <p className={tw`whitespace-nowrap text-sm`}>
              {row?.original?.receiver.primary_contact ?? "N/A"}{" "}
            </p>
            <p className={tw`text-sm`}>
              {row?.original?.receiver.address ?? "N/A"}{" "}
            </p>
          </>
        );
      },
    },
    {
      header: "Finance",
      accessorKey: "deliver_charge",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`whitespace-nowrap`}>
              COD: {row?.original?.cod ?? 0.0}{" "}
            </p>
            <p className={tw`whitespace-nowrap`}>
              Collected: {row?.original?.collected_cod ?? 0.0}
            </p>
          </>
        );
      },
    },
    {
      header: "Destination",
      accessorKey: "destination.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-red-500 `}>
              {row?.original?.is_transit ? "Transit" : null}
            </p>
          </>
        );
      },
    },
    {
      header: "Product Type",
      accessorKey: "package_type.name",
      cell: ({ row, getValue }: any) => {
        return <p className={tw`capitalize`}> {getValue()}</p>;
      },
    },
    {
      header: "Service Type",
      accessorKey: "service_type.name",
      cell: ({ row, getValue }: any) => {
        return (
          <p className={tw`capitalize whitespace-nowrap`}> {getValue()}</p>
        );
      },
    },

    ...(hasAccess(["can_edit_invoice_piece"], userPermissions)
      ? [
          {
            header: "Piece",
            accessorKey: "total_piece",
            cell: ({ row, getValue, table, column }) => {
              return (
                <InputCell
                  row={row}
                  getValue={getValue}
                  searchParams={searchParams}
                  dataKey={"piece"}
                  setSearchParams={setSearchParms}
                  table={table}
                  column={column}
                />
              );
            },
            meta: {
              isEditable: true,
            },
          },
        ]
      : [
          {
            header: "Piece",
            accessorKey: "total_piece",
            cell: ({ row, getValue }: any) => {
              return (
                <>
                  <p>{getValue() ?? 0}</p>
                </>
              );
            },
          },
        ]),
    ...(hasAccess(["can_edit_invoice_weight"], userPermissions)
      ? [
          {
            header: "Weight(kg)",
            accessorKey: "total_weight",
            cell: ({ row, getValue, table, column }) => {
              return (
                <InputCell
                  row={row}
                  getValue={getValue}
                  searchParams={searchParams}
                  dataKey={"weight"}
                  setSearchParams={setSearchParms}
                  table={table}
                  column={column}
                />
              );
            },
            meta: {
              isEditable: true,
            },
          },
        ]
      : [
          {
            header: "Weight(kg)",
            accessorKey: "total_weight",
            cell: ({ row, getValue }: any) => {
              return (
                <>
                  <p>{getValue() ?? 0}</p>
                </>
              );
            },
          },
        ]),
    {
      header: "Branch Charge",
      accessorKey: "branch_charge",
      cell: ({ row, getValue, table, column }: any) => {
        return (
          <>
            <InputCell
              row={row}
              getValue={getValue}
              searchParams={searchParams}
              dataKey={"branch_charge"}
              setSearchParams={setSearchParms}
              table={table}
              column={column}
            />
          </>
        );
      },
      meta: {
        isEditable: true,
      },
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p
              className={tw`uppercase px-1 text-center rounded-md`}
              style={{ background: row?.original?.delivery_status?.color }}
            >
              {getValue()}
            </p>
          </>
        );
      },
    },
  ];

  const defaultCargoColumns = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => handleIndividualSelect(row?.original?.id)}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: () => <p className={tw`w-full text-center`}>WAYBILL</p>,
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <div className={tw`cursor-pointer text-center focus:outline-none`}>
            <p className={tw`whitespace-nowrap`}>{getValue() ?? "N/A"}</p>
            <p className={tw`whitespace-nowrap`}>
              {moment(row?.original?.created).format("YY-MM-DD HH:mm:ss")}
            </p>{" "}
          </div>
        );
      },
    },
    {
      header: "Forwarding No.",
      accessorKey: "forwarding_no",
      cell: NACell,
    },
    {
      header: "Sender",
      accessorKey: "sender.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue()}</p>
            <p className={tw`text-[12px]`}>
              {row?.original?.sender?.primary_contact}
            </p>
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue()}</p>
            <p className={tw`text-[12px]`}>
              {row?.original?.receiver?.primary_contact}
            </p>
          </>
        );
      },
    },
    {
      header: "Country",
      accessorKey: "receiver.country",
      cell: NACell,
    },
    {
      header: "Piece",
      accessorKey: "total_piece",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    {
      header: "Weight",
      accessorKey: "charge_wt",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    {
      header: "Created By",
      accessorKey: "created_by",
      cell: NACell,
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p
              className={tw`uppercase px-1 text-center rounded-md`}
              style={{ background: row?.original?.delivery_status?.color }}
            >
              {getValue()}
            </p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.rider?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Total Amount",
      accessorKey: "total_amount",
      cell: ({ row, getValue, table, column }) => {
        return (
          <InputCell
            row={row}
            getValue={getValue}
            searchParams={searchParams}
            dataKey={"flat_amount"}
            setSearchParams={setSearchParms}
            table={table}
            column={column}
          />
        );
      },
      meta: {
        isEditable: true,
      },
    },
    {
      header: "Hub",
      accessorKey: "hub.name",
      cell: NACell,
    },
    {
      header: "P. Type",
      accessorKey: "reference_type.name",
      cell: NACell,
    },
  ];

  const [user, setUser] = useState<any>("");
  const [userTo, setUserTo] = useState<any>("");
  const handleActionMenu = () => {
    const selectedOrder = searchParams.get("selectedItems")?.split(",");
    const editedRaw = searchParams.get("editedOrders");

    if (!selectedOrder || selectedOrder.length === 0) {
      toast.error("Please select at least one order first");
      return;
    }
    const editedMap: Record<
      string,
      Partial<{ branch_charge: string; flat_amount: string }>
    > = {};
    if (editedRaw) {
      const edits = editedRaw.split(",");
      edits.forEach((entry) => {
        const [idPart, fieldPart] = entry.split(":");
        if (idPart && fieldPart) {
          const [field, value] = fieldPart.split("=");
          if (!editedMap[idPart]) {
            editedMap[idPart] = {};
          }
          editedMap[idPart][field] = value;
        }
      });
    }

    const selectedOrdersData = data.filter((order) =>
      selectedOrder.includes(order.id)
    );
    const totalOrder = selectedOrder?.length;
    const totalTransitCharge = selectedOrdersData.reduce((sum, order) => {
      let transitCharge = order?.transit_charge ?? "0";
      transitCharge = parseFloat(transitCharge || 0);
      return sum + transitCharge;
    }, 0);

    const totalBranchCharge = selectedOrdersData.reduce((sum, order) => {
      const edited = editedMap[order.id];
      let branchCharge = edited?.branch_charge ?? order?.branch_charge ?? "0";
      branchCharge = parseFloat(branchCharge || 0);
      return sum + branchCharge;
    }, 0);

    const totalCollectedCOD = selectedOrdersData.reduce((sum, order) => {
      return sum + parseFloat(order?.collected_cod || "0");
    }, 0);

    const totalAmount = selectedOrdersData.reduce((sum, order) => {
      const edited = editedMap[order.id];
      let flatAmount = edited?.flat_amount ?? order?.total_amount ?? "0";
      flatAmount = parseFloat(flatAmount || 0);
      return sum + flatAmount;
    }, 0);

    setInvoiceReport({
      totalCollectedCod: totalCollectedCOD,
      totalBranchCharge: totalBranchCharge,
      totalTransitCharge: totalTransitCharge,
      totalOrder: totalOrder,
      totalAmount: totalAmount,
    });
    setOpenCreateInvoicePopup(!openCreateInvoicePopup);
  };

  const handleRowClick = (row) => {
    console.info();
  };
  const filterParams = watch();

  useEffect(() => {
    if (user && userTo) {
      searchParams.delete("selectedItems");
      searchParams.delete("selectAllItems");
      setSearchParms(searchParams);
    }
  }, [user, userTo]);

  const clientType = getKey("client-type");

  return (
    <>
      <FormLayout
        handleSubmit={handleSubmit}
        handleFormSubmit={() => handleActionMenu()}
        setError={setError}
        buttonLabel={"Create"}
        isLoading={isLoading}
      >
        <div className={tw` mb-4 px-4 grid grid-cols-4 gap-2`}>
          <ComboboxInput
            url={APIS.BRANCH}
            queryKey={"branch"}
            register={register("from_branch")}
            errors={errors?.from_branch}
            name={"from_branch"}
            label={"From Branch"}
            props={{ is_active: true }}
            disabled={!currentUser?.is_superuser}
            setValue={setValue}
            placeholder={"Select Branch Name"}
            getValues={getValues}
            clearErrors={clearErrors}
            setUser={setUser}
          />
          <ComboboxInput
            url={APIS.BRANCH}
            queryKey={"branch"}
            register={register("to_branch")}
            errors={errors?.to_branch}
            name={"to_branch"}
            label={"To Branch"}
            props={{ is_active: true }}
            setValue={setValue}
            placeholder={"Select Branch Name"}
            getValues={getValues}
            clearErrors={clearErrors}
            setUser={setUserTo}
          />
          <DateRangePicker
            label={"Select Date Range"}
            setValue={setValue}
            name={"created__range"}
            getValues={getValues}
          />
          <MultiSelectFilterOrderStatus
            label={"Payment Method"}
            queryKey={"payment-method"}
            params={{
              is_active: true,
            }}
            name={"payment_method__in"}
            placehoder="Select payment method..."
            control={control}
            url={`${APIS.PAYMENT_METHOD_TYPE}`}
          />
        </div>
      </FormLayout>
      <Modal
        isOpen={openCreateInvoicePopup}
        setIsOpen={setOpenCreateInvoicePopup}
        title={"Are You Sure You Want to Create Statement?"}
        children={
          <>
            {clientType === "cargo" ? (
              <CargoBranchInvoicePopup
                handleCreate={handleCreate}
                setError={setError}
                isLoading={isLoading}
                user={user}
                setOrderData={setData}
                invoiceReport={invoiceReport}
                getValues={getValues}
              />
            ) : (
              <CreateBranchForm
                handleCreate={handleCreate}
                setError={setError}
                isLoading={isLoading}
                user={user}
                setOrderData={setData}
                invoiceReport={invoiceReport}
                getValues={getValues}
              />
            )}
          </>
        }
      />
      <Modal
        title={"Update Delivery Charge"}
        isOpen={openDeliveryModal}
        setIsOpen={setOpenDeliveryModal}
        children={
          <>
            <div className={tw`w-[400px]`}>
              <EditDeliveryCharge
                id={id}
                type={"branch"}
                openDeliveryModal={openDeliveryModal}
                setOpenDeliveryModal={setOpenDeliveryModal}
                chargevalue={chargevalue}
              />
            </div>
          </>
        }
      />
      {FromBranch && ToBranch ? (
        <InvoiceDataTable
          url={APIS.BRANCH_ORDER}
          queryKey={"orders"}
          columns={
            clientType === "cargo" ? defaultCargoColumns : defaultColumns
          }
          tableName={"branch-orders"}
          isUpload={true}
          isDownload={true}
          setOrderData={setData}
          handleRowClick={handleRowClick}
          params={{
            ...filterParams,
            payment_method__in: filterParams?.payment_method__in
              ?.map((items) => items?.value)
              .join(","),
            branch_invoice_id__isnull: true,
            is_create_invoice: true,
          }}
          actionMenu={
            <>
              <div className={tw`w-full flex justify-end`}>
                <div className={tw`w-24`}>
                  <Button
                    label={"Draft"}
                    isLoading={isDraftSaveLoading}
                    onClick={() => handleSaveDraft()}
                  />
                </div>
              </div>
            </>
          }
        />
      ) : null}
    </>
  );
};

export default CreateBranchInvoice;
